import { escapeHtml } from "./router.js";

export { escapeHtml };

export function fmtDate(value) {
  if (!value) return "—";
  try {
    const d = typeof value === "string" && value.length === 10 ? new Date(value + "T00:00:00") : new Date(value);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export function fmtDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function fmtPct(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(digits).replace(".", ",")}%`;
}

export function fmtNumber(value, digits = 1) {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("pt-BR", { maximumFractionDigits: digits });
}

export function fmtInt(value) {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("pt-BR");
}

export function houseLabel(house) {
  return house === "senado" ? "Senado" : house === "camara" ? "Câmara" : house || "";
}

export function housePill(house) {
  return `<span class="pill pill--${escapeHtml(house)}">${houseLabel(house)}</span>`;
}

export function memberNoun(house, plural = true) {
  if (house === "senado") return plural ? "senadores" : "senador";
  return plural ? "deputados" : "deputado";
}

export function propositionLabel(p) {
  if (!p) return "";
  if (p.identification) return p.identification;
  return [p.type_sigla, p.number, p.year ? `/${p.year}` : ""].filter(Boolean).join(" ").replace(" /", "/");
}

export function propositionUrl(p) {
  return p?.id ? `/proposicao/${p.id}` : null;
}

export function memberUrl(m) {
  return m?.id ? `/parlamentar/${m.id}` : null;
}

export function rollCallUrl(r) {
  return r?.id ? `/votacao/${r.id}` : null;
}

export function resultLabel(result) {
  const r = (result || "").toLowerCase();
  if (r === "aprovado" || r.includes("aprov")) return { text: "Aprovado", variant: "ok" };
  if (r === "rejeitado" || r.includes("rejeit") || r.includes("prejudic")) return { text: "Rejeitado", variant: "no" };
  if (r) return { text: result, variant: "neutral" };
  return { text: "Sem resultado", variant: "neutral" };
}

const VOTE_LABELS = {
  sim: "Sim",
  nao: "Não",
  abstencao: "Abstenção",
  obstrucao: "Obstrução",
  art17: "Art. 17",
  presidente: "Presidiu",
  presente_nao_votou: "Presente",
  ausencia_justificada: "Ausência just.",
  ausente: "Ausente",
  outro: "Outro",
  liberado: "Liberado",
};

export function voteLabel(v) {
  if (!v) return "—";
  return VOTE_LABELS[v] || v;
}

export function voteVariant(v) {
  if (!v) return "neutral";
  if (v === "sim") return "ok";
  if (v === "nao") return "no";
  if (v === "abstencao") return "abst";
  if (v === "obstrucao") return "obstr";
  if (v === "ausente" || v === "ausencia_justificada") return "absent";
  if (v === "presidente" || v === "art17") return "pres";
  if (v === "liberado") return "free";
  return "neutral";
}

/** Aprovado / Rejeitado pill for a roll-call result; empty string when there is no result. */
export function resultBadge(result, extraClass = "") {
  if (!result) return "";
  const r = resultLabel(result);
  return `<span class="pill pill--${r.variant} ${extraClass}">${escapeHtml(r.text)}</span>`;
}

export function pluralize(n, singular, plural) {
  return Number(n) === 1 ? singular : plural || `${singular}s`;
}

export function votePill(v, extraClass = "") {
  if (!v) return `<span class="pill pill--neutral ${extraClass}">—</span>`;
  return `<span class="pill pill--${voteVariant(v)} ${extraClass}">${escapeHtml(voteLabel(v))}</span>`;
}

export function outlookLabel(o) {
  const map = { alta: "Alta", media: "Média", baixa: "Baixa", indeterminada: "Indeterminada", nao_aplicavel: "Não se aplica" };
  return map[o] || o || "—";
}

export function outlookVariant(o) {
  return { alta: "ok", media: "warn", baixa: "no" }[o] || "neutral";
}

export function agreeMark(flag) {
  if (flag === true) return `<span class="mark mark--yes" title="De acordo">✓</span>`;
  if (flag === false) return `<span class="mark mark--no" title="Divergente">✗</span>`;
  return `<span class="mark mark--na" title="Não se aplica">·</span>`;
}

export function truncate(text, max = 220) {
  if (!text) return "";
  const t = String(text).trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + "…" : t;
}

export function daysLeftLabel(n) {
  if (n === null || n === undefined) return "";
  if (n < 0) return `vencido há ${Math.abs(n)} dia${Math.abs(n) === 1 ? "" : "s"}`;
  if (n === 0) return "vence hoje";
  return `faltam ${n} dia${n === 1 ? "" : "s"}`;
}

export function sourceMixLabel(mix) {
  if (!mix) return null;
  const parts = Object.fromEntries(
    String(mix)
      .split(",")
      .map((p) => p.split(":"))
      .map(([k, v]) => [k?.trim(), Number(v) || 0]),
  );
  const official = parts.official || 0;
  const proxy = (parts.proxy_leader || 0) + (parts.proxy_coalition || 0);
  if (official && !proxy) return { text: "oficial", variant: "ok" };
  if (proxy && !official) return { text: "proxy", variant: "warn" };
  if (official || proxy) return { text: `oficial ${official} · proxy ${proxy}`, variant: "warn" };
  return null;
}

export function currentLegislature() {
  const y = new Date().getFullYear();
  return 57 + Math.floor((y - 2023) / 4);
}

export function legislatureRange(n) {
  const start = 2023 + (n - 57) * 4;
  return `${start}–${start + 4}`;
}
